import {
  Box,
  Button,
  FormComponent,
  type SelectOption,
  Text,
} from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

interface Props {
  noOfEmployees: number
  onConfirm: (closePortal: () => void) => Promise<void>
  onClose: () => void
  form: Form
  options: SelectOption[]
  canArchive: boolean
  canReassignAll: boolean
  canAssignEmployees: boolean
  loading: boolean
}

const ArchiveConfirmationModal = ({
  noOfEmployees,
  onConfirm,
  onClose,
  form,
  options,
  canArchive,
  canReassignAll,
  canAssignEmployees,
  loading,
}: Props) => {
  const count = {
    count: noOfEmployees,
  }

  const noPermissions = !canAssignEmployees

  const title =
    noPermissions || !canReassignAll
      ? i18n.t('settings.work_schedule.table_data.archive.not_allowed_title')
      : i18n.t('settings.work_schedule.table_data.archive.title')

  const description = noOfEmployees
    ? noPermissions
      ? i18n.t('settings.work_schedule.table_data.archive.description.no_permissions') // No permission
      : canReassignAll
        ? i18n.tp('settings.work_schedule.table_data.archive.description', count) // Can archive
        : i18n.t('settings.work_schedule.table_data.archive.description.mixed_employees') // Mixed teams
    : i18n.t('settings.work_schedule.table_data.archive.description.zero') // No employees

  const disabled = !canArchive || (!form.get('work_schedule_id').value && noOfEmployees > 0)

  const footer = !canArchive ? (
    <Button type="primary" onClick={onClose}>
      {i18n.t('settings.work_schedule.table_data.archive.not_allowed_submit')}
    </Button>
  ) : (
    <>
      <Button type="secondary" onClick={onClose}>
        {i18n.t('settings.work_schedule.cancel')}
      </Button>
      <Button disabled={disabled} submit>
        {i18n.t('settings.work_schedule.table_data.archive.submit')}
      </Button>
    </>
  )

  return (
    <Modal
      illustration="archive"
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={onConfirm}
      footer={footer}
    >
      {loading ? (
        <Loading />
      ) : (
        canArchive &&
        noOfEmployees > 0 && (
          <>
            <FormComponent padding={null}>
              <Select
                none={i18n.t('common.none')}
                field={form.get('work_schedule_id')}
                label={i18n.t('settings.work_schedule.table_data.archive.labels.reassign')}
                options={options}
              />
              <Box paddingY="s16">
                <DayPicker
                  field={form.get('effective_at')}
                  label={i18n.t('settings.work_schedule.table_data.archive.labels.effective')}
                />
              </Box>
              <Text.Normal>
                {i18n.t('settings.work_schedule.employees_tab.employee_modal.footer_description')}
              </Text.Normal>
            </FormComponent>
          </>
        )
      )}
    </Modal>
  )
}

export default observer(ArchiveConfirmationModal)
